// ── What the chat DOES with a task (chat Figma rebuild WP3, frames 08–17, gaps B7–B9 / S8) ─────
//
// The two task blocks of an answer act through here:
//   • `createFromSuggestion` — "Create" under a suggested task (frame 09 → 11): the task is made,
//     it gets its Ongoing the way a task made in the box does (or joins the suggestion's own), and
//     the block turns into "Task created · …" in place (`timelineSearch/patchBlock`);
//   • `viewTask` — "View Task" / "View" (frames 10, 14): the box opens the task (or the draft) and
//     the chat DOCK dims the page round it (store/modules/chatDock.js, Home/ChatDock.vue);
//   • `refreshCreated` — the docked task was updated (frame 13): the block re-reads it;
//   • `noteCreated` — a "Task created" answer arrived: what it made is recorded on the answer
//     (`POST v1/chat/messages/{id}/saved`, the rail's child line — WP5 draws it) and the Ongoing it
//     touched becomes news in the left list (lib/chatFresh.js, components/common/OngoingSection.vue).
//
// The rules themselves are pure (lib/chatTaskBlocks.js, lib/chatFresh.js); this file only sequences
// the store and the wire.

import api from '@/lib/services/general.service'
import { savedRefsFrom } from '@/lib/chatSession'
import {
  addFresh, clearFresh, freshOngoingIds as freshIdsOf, readFresh, writeFresh,
} from '@/lib/chatFresh'
import {
  answerMessageId, createdFromTask, createdPatchFromOps, createdPatchFromTask, dockEditDetail,
  isRecentTurn, isServerId, withFallbackText,
} from '@/lib/chatTaskBlocks'

export const EDIT_EVENT = 'smartbox-edit-entity'

export const state = () => ({
  // lib/chatFresh.js's record — mirrored to sessionStorage on every change.
  fresh: readFresh(),
  // `<messageId|turnId>:<blockKey>` → true for every created block already noted, so an answer that
  // is drawn again (Home re-mounted) neither re-posts its saved ref nor re-marks its news.
  noted: {},
})

export const getters = {
  freshEntries: (s) => s.fresh,
  /** The Ongoing ids that are news, newest first (read against the loaded Ongoing list). */
  freshOngoingIds: (s, g, rootState, rootGetters) => freshIdsOf(s.fresh, rootGetters?.['ongoing/items'] || []),
}

/** The block a turn holds under `key`, from the chat's own state. */
function findBlock(rootState, turnId, key) {
  const turn = (rootState?.timelineSearch?.conversation || []).find((t) => t && t.id === turnId)
  const block = Array.isArray(turn?.blocks) ? turn.blocks.find((b) => b && b.key === key) : null
  return { turn: turn || null, block: block || null }
}

/** Patch a block in place, its plain text brought along. */
function patchBlock({ dispatch, rootState }, turnId, key, patch) {
  const { block } = findBlock(rootState, turnId, key)
  if (!block || !patch) return
  dispatch('timelineSearch/patchBlock', { turnId, key, patch: withFallbackText(block, patch) }, { root: true })
}

export const actions = {
  /** Mark `{ ongoingId?, taskId?, title? }` as news in the left Ongoing list. */
  markFresh({ commit, state: s }, ref) {
    commit('SET_FRESH', addFresh(s.fresh, ref))
  },

  /** That Ongoing was opened — its news has been read. */
  clearFresh({ commit, state: s, rootGetters }, ongoingId) {
    if (!ongoingId) return
    const items = rootGetters?.['ongoing/items'] || []
    const before = s.fresh
    const next = clearFresh(before, ongoingId, items)
    if (JSON.stringify(next) !== JSON.stringify(before)) commit('SET_FRESH', next)
  },

  /**
   * Record what an answer produced on the answer itself (`POST v1/chat/messages/{id}/saved`,
   * busy-main-bff ChatController.Saved — additive and idempotent). Defensive by design: only real
   * ids go on the wire, guests have no journal, and any refusal (a 404 for an answer the journal
   * never wrote, incognito, a BFF that predates the route) is swallowed — the chat on screen
   * already says what was made. The live turn carries the ref too, for the rail (WP5).
   */
  async recordSaved({ commit, rootState, rootGetters }, { turnId, messageId, taskId, connectedOngoingId } = {}) {
    // …and a CONNECTION the user made from a connections table (frame 21): `connected:<ongoingId>`,
    // which the BFF's rail summary reads back as "Connected to <Ongoing>" (Core/ChatSummary.cs).
    const refs = [
      ...savedRefsFrom({ taskId }),
      ...(isServerId(connectedOngoingId) ? [`connected:${connectedOngoingId}`] : []),
    ]
    if (!refs.length) return false
    const { turn } = findBlock(rootState, turnId, null)
    if (turn) commit('timelineSearch/UPDATE_TURN', { id: turnId, savedRefs: [...new Set([...(turn.savedRefs || []), ...refs])] }, { root: true })
    if (!isServerId(messageId) || rootGetters?.['user/isAuthenticated'] === false) return false
    try {
      await api.insertWithPath('chat', `messages/${messageId}/saved`, { refs })
      return true
    } catch {
      return false
    }
  },

  /**
   * A "Task created" block is on screen. Once per block: record its ref on the answer, and — when
   * the answer is from this conversation, today — make the Ongoing it touched news (its own Ongoing
   * when the task was attached to one, frame 17; else the Ongoing the task started, frame 08).
   */
  async noteCreated({ commit, dispatch, state: s }, { turn, block } = {}) {
    if (!block || block.type !== 'entityCreated' || !block.id) return
    const id = `${answerMessageId(turn) || turn?.id || ''}:${block.key || ''}`
    if (s.noted[id]) return
    commit('NOTE', id)
    if (isRecentTurn(turn)) {
      // The BFF names the Ongoing a task started for itself (`startedOngoingId`, chat rebuild
      // integration); without it the title is what finds it in the list.
      const ongoingId = block.ongoing?.id || block.startedOngoingId || null
      await dispatch('markFresh', { ongoingId, taskId: block.id, title: ongoingId ? null : block.title })
      // The Ongoing the server made (or moved) for it is not in the list loaded before the question —
      // and the BFF's cached list does not know either (the ask wrote it without touching an ongoing
      // endpoint, so nothing bumped the list version): re-read past the cache (`reconcile`).
      try { await dispatch('ongoing/reconcile', null, { root: true }) } catch { /* the list keeps what it had */ }
    }
    await dispatch('recordSaved', { turnId: turn?.id, messageId: answerMessageId(turn), taskId: block.id })
  },

  /**
   * "Create" under a suggested task (frame 09 → 11). The task is made exactly as the box makes one
   * on Home — and then given its place: joined to the suggestion's Ongoing when it names one, else
   * starting its own (SmartBox `startOngoingFromEntity`), which is the row the left list then shows
   * as news. The block becomes "Task created · …" in place. A refused create puts the block back
   * with an `error`, so Create can be pressed again.
   */
  async createFromSuggestion(ctx, { turnId, key } = {}) {
    const { dispatch, rootState, rootGetters } = ctx
    const { turn, block } = findBlock(rootState, turnId, key)
    if (!block || block.type !== 'suggestion' || block.state === 'creating') return null
    patchBlock(ctx, turnId, key, { state: 'creating', error: null })
    let task = null
    try {
      task = await dispatch('task/addTask', {
        silentToast: true,
        title: String(block.title || '').trim(),
        content: block.detail ? `<p>${String(block.detail)}</p>` : null,
        isImportant: false,
        completedDate: null,
        status: 1,
        dueDate: block.dueUtc || null,
        isAllDay: !!(block.dueUtc && block.allDay),
        remindDate: null,
      }, { root: true })
    } catch {
      task = null
    }
    if (!task || !task.id) {
      patchBlock(ctx, turnId, key, { state: null, error: 'create' })
      return null
    }
    let ongoing = null
    try {
      if (block.ongoingId) {
        if (task.timelineId) await dispatch('ongoing/linkTimeline', { id: block.ongoingId, timelineId: task.timelineId }, { root: true })
        ongoing = rootGetters?.['ongoing/byId']?.(block.ongoingId) || { id: block.ongoingId }
      } else {
        const made = await dispatch('ongoing/createFromEntity', { title: task.title || block.title, story: task.title || block.title, origin: 'task', timelineId: task.timelineId }, { root: true })
        if (made?.id) await dispatch('markFresh', { ongoingId: made.id, taskId: task.id })
      }
    } catch {
      // The task exists — its Ongoing is the follow-up, and a refused one must not undo the block.
    }
    if (ongoing?.id) await dispatch('markFresh', { ongoingId: ongoing.id, taskId: task.id })
    await dispatch('adoptCreated', { turnId, key, task, ongoing, turn })
    return task
  },

  /**
   * A suggestion's task now exists — made by Create, or saved from the box after View (frame 10's
   * "Create Task"). The block becomes the created one and its ref is recorded on the answer.
   */
  async adoptCreated(ctx, { turnId, key, task, ongoing = null } = {}) {
    const { commit, dispatch, rootState } = ctx
    const { turn, block } = findBlock(rootState, turnId, key)
    if (!block || !task?.id) return
    const next = createdFromTask(task, block, ongoing)
    patchBlock(ctx, turnId, key, { ...next, error: null })
    commit('NOTE', `${answerMessageId(turn) || turnId || ''}:${key || ''}`)
    if (!ongoing?.id) await dispatch('markFresh', { taskId: task.id, title: next.title })
    await dispatch('recordSaved', { turnId, messageId: answerMessageId(turn), taskId: task.id })
  },

  /**
   * "View Task" / "View" (frames 10, 14, 15): the task opens in the box and the page dims round it.
   * The dock remembers WHICH block it is about, so the block reads "Viewing" and a save lands on it.
   */
  viewTask({ dispatch, rootState }, { turnId, key } = {}) {
    const { block } = findBlock(rootState, turnId, key)
    if (!block) return false
    const suggestion = block.type === 'suggestion'
    dispatch('chatDock/open', {
      mode: 'task',
      ref: { turnId, key, id: suggestion ? null : (block.id || null), suggestion },
    }, { root: true })
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(EDIT_EVENT, { detail: dockEditDetail(block) }))
    return true
  },

  /**
   * The docked task was updated (frame 13). The block takes the update at once from what was sent,
   * then from the task re-read — the server's word is the last one.
   */
  async refreshCreated(ctx, { turnId, key, id, ops } = {}) {
    const { dispatch } = ctx
    const sent = createdPatchFromOps(ops)
    if (sent) patchBlock(ctx, turnId, key, sent)
    if (!id) return null
    let task = null
    try { task = await dispatch('task/fetchTaskById', id, { root: true }) } catch { task = null }
    const read = createdPatchFromTask(task)
    if (read) patchBlock(ctx, turnId, key, read)
    return read || sent
  },
}

export const mutations = {
  SET_FRESH(s, entries) {
    s.fresh = Array.isArray(entries) ? entries : []
    writeFresh(s.fresh)
  },
  NOTE(s, id) {
    s.noted = { ...s.noted, [id]: true }
  },
}
